/**
 * The users half of /admin — list, invite, and the account actions.
 */
import { useCallback, useState } from 'react';
import {
  fetchAdminUsers,
  forceTotpReenrol,
  inviteUser,
  issuePasswordReset,
  revokeUserSessions,
  setUserActive,
  setUserAdmin,
} from '../../api/endpoints';
import { toApiError } from '../../api/errors';
import type { AdminUser, Project } from '../../api/types';
import { useAuth } from '../../auth/AuthProvider';
import { ErrorNotice, Notice } from '../../components/ui/Notice';
import { EmptyState } from '../../components/ui/EmptyState';
import { ListSkeleton, Spinner } from '../../components/ui/Skeleton';
import { useResource } from '../../hooks/useResource';
import { OneTimeSecret } from './OneTimeSecret';
import { UserCard } from './UserCard';
import { RemoveMemberDialog } from './RemoveMemberDialog';
import { UserProjects } from './UserProjects';
import { useMemberships } from './useMemberships';

type Handout = { title: string; token: string; expiresAt: string } | null;

const FIELD =
  'h-11 w-full rounded-md border border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-3 text-base outline-none focus:border-[color:var(--color-accent)]';

export function AdminUsersPanel() {
  const { state } = useAuth();
  const users = useResource<AdminUser[]>((signal) => fetchAdminUsers(signal), []);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [handout, setHandout] = useState<Handout>(null);

  const currentUserId = state.status === 'authenticated' ? state.user.id : '';

  /**
   * PH-56 — loaded ONCE for the whole panel, not per card.
   *
   * ⚠️ `enabled` is tied to the user list arriving: there is no point reading nine projects'
   * membership before we know whether there is anybody to show it against, and on a 403 (a
   * non-admin who reached this route) it means we never make ten pointless requests.
   */
  const memberships = useMemberships(Boolean(users.data));

  /**
   * The removal under consideration, or null. ⭐ Holding the USER and PROJECT rather than ids: the
   * dialog needs both names to say what is about to happen, and looking them up again inside the
   * dialog is how the two halves drift apart.
   */
  const [removing, setRemoving] = useState<{ user: AdminUser; project: Project } | null>(null);

  /**
   * ⭐⭐ EVERY MUTATION RELOADS THE LIST, and none of them edits the local array. An optimistic
   *   in-place update would be showing the admin what this client BELIEVES rather than what the
   *   server did — and on this screen the two can genuinely differ: db/005's trigger refuses to
   *   remove the last admin, so "revoke admin" can succeed in the UI and be refused by the database.
   *   ⇒ The row an admin reads after acting is always the row the server actually holds.
   */
  const run = useCallback(
    async (user: AdminUser, path: string, action: () => Promise<void>) => {
      setBusyId(user.id);
      setActionError(null);
      try {
        await action();
        users.reload();
      } catch (err) {
        // ⭐ The API's own message, which for the last-admin case is a 409 explaining exactly why.
        setActionError(toApiError(err, path).message);
      } finally {
        setBusyId(null);
      }
    },
    [users],
  );

  return (
    <div className="space-y-4">
      <InviteForm
        onInvited={(invite) =>
          setHandout({
            title: `Invitation for ${invite.email}`,
            token: invite.invite_token,
            expiresAt: invite.expires_at,
          })
        }
        onError={setActionError}
        onDone={() => users.reload()}
      />

      {handout ? (
        <OneTimeSecret
          title={handout.title}
          token={handout.token}
          expiresAt={handout.expiresAt}
          onDismiss={() => setHandout(null)}
        />
      ) : null}

      {actionError ? <Notice tone="error" title={actionError} /> : null}

      {/* ⭐ Stale and current must not look the same — the house rule from useResource. */}
      {users.isStale && users.error ? (
        <Notice
          tone="warning"
          title="This list may be out of date"
          action={
            <button className="text-xs underline" onClick={users.reload}>
              Retry
            </button>
          }
        >
          The last refresh failed{users.loadedAt ? `; showing data from ${users.loadedAt.toLocaleTimeString()}` : ''}.
        </Notice>
      ) : null}

      {users.isLoading ? <ListSkeleton rows={5} /> : null}
      {users.error && !users.data ? <ErrorNotice error={users.error} onRetry={users.reload} /> : null}

      {users.data && users.data.length === 0 ? (
        <EmptyState title="No accounts yet" detail="Invite someone with the form above." />
      ) : null}

      {users.data && users.data.length > 0 ? (
        <ul className="space-y-2">
          {users.data.map((user) => (
            <UserCard
              key={user.id}
              user={user}
              currentUserId={currentUserId}
              busy={busyId === user.id}
              /* PH-56 — which projects this person is in. ⚠️ `byUser.get` returning undefined is
                 MEANINGFUL: it is "in no project", the state that produced every confusing
                 permission error in PlanHub. UserProjects renders it as a sentence. */
              renderProjects={(u) => (
                <UserProjects
                  user={u}
                  memberships={memberships.byUser.get(String(u.id))}
                  allProjects={memberships.projects}
                  busy={memberships.busyUserId === String(u.id)}
                  loading={!memberships.hasLoaded}
                  onAdd={(projectId, role) => void memberships.add(u.id, projectId, role)}
                  /* ⛔ CONFIRMATION IS A STATE, not a `window.confirm` — this codebase's own rule,
                     written in useCompleteSprint.ts. Nothing is sent until the dialog confirms. */
                  onRemove={(projectId) => {
                    const project = memberships.projects.find(
                      (p) => String(p.id) === String(projectId),
                    );
                    if (project) setRemoving({ user: u, project });
                  }}
                />
              )}
              onSetActive={(u, active) =>
                run(u, `/admin/users/${u.id}/active`, () => setUserActive(u.id, active))
              }
              onSetAdmin={(u, grant) =>
                run(u, `/admin/users/${u.id}/admin`, () => setUserAdmin(u.id, grant))
              }
              onForceReenrol={(u) =>
                run(u, `/admin/users/${u.id}/totp/reset`, () => forceTotpReenrol(u.id))
              }
              onRevokeSessions={(u) =>
                run(u, `/admin/users/${u.id}/sessions/revoke`, () => revokeUserSessions(u.id))
              }
              onIssueReset={(u) =>
                run(u, `/admin/users/${u.id}/password-reset`, async () => {
                  const reset = await issuePasswordReset(u.id);
                  setHandout({
                    title: `Password reset for ${u.email}`,
                    token: reset.reset_token,
                    expiresAt: reset.expires_at,
                  });
                })
              }
            />
          ))}
        </ul>
      ) : null}

      {removing ? (
        <RemoveMemberDialog
          user={removing.user}
          project={removing.project}
          warning={memberships.warnBeforeRemove(removing.user.id, removing.project.id)}
          busy={memberships.busyUserId === String(removing.user.id)}
          error={memberships.error}
          onCancel={() => setRemoving(null)}
          /* ⭐ Closed only on SUCCESS. `remove` resolves either way, so the dialog checks whether an
             error landed before dismissing — otherwise a refusal would close the one surface able to
             show it, which is the failure `useCompleteSprint` warns about. */
          onConfirm={() => {
            const { user, project } = removing;
            void memberships.remove(user.id, project.id).then((ok) => {
              if (ok) setRemoving(null);
            });
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * ⭐ The invite form states what it does NOT do. "No password is set here" is worth a line, because
 *   the obvious mental model of "create a user" involves typing one — and an admin who knows the
 *   invitee's password is a worse position than one who never learned it.
 */
function InviteForm({
  onInvited,
  onError,
  onDone,
}: {
  onInvited: (invite: { email: string; invite_token: string; expires_at: string }) => void;
  onError: (message: string | null) => void;
  onDone: () => void;
}) {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    onError(null);
    try {
      const invite = await inviteUser(email.trim().toLowerCase(), displayName.trim());
      onInvited(invite);
      setEmail('');
      setDisplayName('');
      onDone();
    } catch (err) {
      onError(toApiError(err, '/admin/users/invite').message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="space-y-3 rounded-md border border-[color:var(--color-line)] bg-[color:var(--color-surface)] p-3"
    >
      <div>
        <p className="text-sm font-semibold">Invite a local account</p>
        <p className="mt-0.5 text-xs text-[color:var(--color-ink-muted)]">
          For people who are not in GitLab. You do not set their password — they choose it when they
          accept, and they must enrol an authenticator before they can sign in.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="space-y-1">
          <span className="block text-xs font-medium text-[color:var(--color-ink-muted)]">Email</span>
          <input
            className={FIELD}
            type="email"
            inputMode="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <label className="space-y-1">
          <span className="block text-xs font-medium text-[color:var(--color-ink-muted)]">Name</span>
          <input
            className={FIELD}
            type="text"
            required
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </label>
      </div>

      <button
        type="submit"
        className="flex h-11 items-center justify-center gap-2 rounded-md bg-[color:var(--color-accent)] px-4 text-sm font-semibold text-[color:var(--color-on-accent)] hover:brightness-110 disabled:pointer-events-none disabled:opacity-50"
        disabled={busy || !email || !displayName}
      >
        {busy ? <Spinner label="Inviting" /> : null}
        Create invitation
      </button>
    </form>
  );
}
